import { Database, Table, Calculator, ChevronRight, Check, Eye } from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { getConnections, getConnectionTables, Connection, DBTable } from '@/api/datamodels';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import React, { useState, useEffect } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';

export const DataModelSidebar = ({
    selectedConnection: externalConnection,
    onConnectionChange: externalOnConnectionChange
}: {
    selectedConnection?: string;
    onConnectionChange?: (connectionId: string) => void;
} = {}) => {
    const { token } = useAuth();
    const [connections, setConnections] = useState<Connection[]>([]);
    const [selectedConnection, setSelectedConnection] = useState<string>(externalConnection || '');
    const [tables, setTables] = useState<DBTable[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [previewTable, setPreviewTable] = useState<string | null>(null);

    // Sync with external prop when it changes
    useEffect(() => {
        if (externalConnection && externalConnection !== selectedConnection) {
            setSelectedConnection(externalConnection);
            handleConnectionChange(externalConnection);
        }
    }, [externalConnection]);

    useEffect(() => {
        if (token) {
            loadConnections();
        }
    }, [token]);

    const loadConnections = async () => {
        try {
            const data = await getConnections(token!);
            setConnections(data);
        } catch (e) {
            console.error("Failed to load connections", e);
        }
    };

    const handleConnectionChange = async (connId: string) => {
        setSelectedConnection(connId);
        if (externalOnConnectionChange) {
            externalOnConnectionChange(connId);
        }
        setIsLoading(true);
        try {
            const schemaData = await getConnectionTables(connId, token!);
            // Access 'tables' from the response. The API returns { schemas: [...], tables: [...] }
            // Adjust based on actual API response structure inspection if needed.
            // Looking at backend/modules/connections/crud.py -> get_database_schemas usually returns { tables: [...] } or just a list?
            // Let's assume it returns a list or standard object.
            // Update: backend/modules/connections/crud.py's get_database_schemas returns dict with 'tables' key usually.
            // Let's safe guard.
            const tableList = schemaData.tables || schemaData || [];

            // Normalize
            const formattedTables = Array.isArray(tableList) ? tableList.map((t: any) => ({
                name: typeof t === 'string' ? t : t.name,
                schema: t.schema
            })) : [];

            setTables(formattedTables);
        } catch (e) {
            console.error("Failed to load tables", e);
            setTables([]);
        } finally {
            setIsLoading(false);
        }
    };

    const onDragStart = (event, nodeType, payload) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.setData('application/payload', JSON.stringify(payload));
        event.dataTransfer.effectAllowed = 'move';
    };

    // Helper to generate a short table alias from full table name
    const generateTableAlias = (tableName: string): string => {
        // Remove common prefixes like 'dbo_', 'tbl_', etc.
        const cleaned = tableName.replace(/^(dbo_|tbl_|vw_|dim_|fact_)/i, '');

        // Split by underscores or camel case and take first letters
        const parts = cleaned.split(/[_.]/).filter(p => p.length > 0);

        if (parts.length > 1) {
            // Multi-word: take first letter of each word
            return parts.map(p => p[0].toLowerCase()).join('');
        } else {
            // Single word: take first 2-3 letters
            const word = parts[0];
            if (word.length <= 3) return word.toLowerCase();

            // For CamelCase, extract capitals
            const capitals = word.match(/[A-Z]/g);
            if (capitals && capitals.length > 1) {
                return capitals.slice(0, 3).join('').toLowerCase();
            }

            // Default: first 3 letters
            return word.substring(0, 3).toLowerCase();
        }
    };

    const filteredTables = tables.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <aside className="w-72 bg-white border-r border-gray-200 flex flex-col h-full z-10">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
                <h2 className="font-semibold text-gray-800 text-sm mb-3">Data Sources</h2>

                <div className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Connection</label>
                        <Select value={selectedConnection} onValueChange={handleConnectionChange}>
                            <SelectTrigger className="w-full h-8 text-xs bg-white">
                                <SelectValue placeholder="Select a connection..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {connections.map(c => (
                                    <SelectItem key={c.id} value={c.id} className="text-xs">
                                        {c.name} <span className="text-gray-400 ml-1">({c.provider})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedConnection && (
                        <Input
                            placeholder="Search tables..."
                            className="h-7 text-xs bg-white"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    )}
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-3 space-y-1">
                    {!selectedConnection && (
                        <div className="text-center py-8 text-xs text-gray-400 px-4">
                            Select a connection to browse tables and build your model.
                        </div>
                    )}

                    {selectedConnection && isLoading && (
                        <div className="text-center py-8 text-xs text-gray-400 animate-pulse">
                            Loading tables...
                        </div>
                    )}

                    {selectedConnection && !isLoading && filteredTables.length === 0 && (
                        <div className="text-center py-8 text-xs text-gray-400">
                            No tables found.
                        </div>
                    )}

                    {filteredTables.map((table, idx) => (
                        <div
                            key={`${table.schema}-${table.name}-${idx}`}
                            className="flex items-center gap-2 p-2 bg-white border border-transparent hover:border-blue-200 hover:bg-blue-50 rounded cursor-grab group transition-all"
                            onDragStart={(event) => onDragStart(event, 'table', {
                                type: 'PHYSICAL',
                                source_table: table.name,
                                label: generateTableAlias(table.name), // Smart alias: dbo_FactCharge -> fc
                                fullTableName: table.name, // Keep full name for display
                                source_connection_id: selectedConnection
                            })}
                            draggable
                        >
                            <div className="p-1 rounded bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                <Table size={14} />
                            </div>
                            <div className="flex flex-col overflow-hidden flex-1">
                                <span className="text-xs font-medium text-gray-700 truncate">{table.name}</span>
                                {table.schema && <span className="text-[9px] text-gray-400 truncate">{table.schema}</span>}
                            </div>

                            {/* Preview Eye Icon */}
                            <button
                                onClick={(e) => { e.stopPropagation(); setPreviewTable(table.name); }}
                                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 hover:bg-white rounded transition-all z-10 shrink-0"
                                title="Preview Table Data"
                            >
                                <Eye size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            </ScrollArea>

            {/* Legacy Virtual Table Drag Item (Fixed at bottom or separate tab?) */}
            <div className="p-3 border-t border-gray-100 bg-gray-50">
                <div
                    className="flex items-center gap-2 p-2 bg-white border border-gray-200 border-dashed rounded cursor-grab hover:border-purple-300 hover:shadow-sm transition-all"
                    onDragStart={(event) => onDragStart(event, 'table', { type: 'VIRTUAL', label: 'New Virtual Table' })}
                    draggable
                >
                    <div className="p-1.5 bg-purple-50 rounded text-purple-600">
                        <Database size={14} />
                    </div>
                    <span className="text-xs font-medium text-gray-700">Add Virtual Table (SQL)</span>
                </div>
            </div>

            {/* DATA PREVIEW MODAL */}
            <DataPreviewModal
                isOpen={!!previewTable}
                onClose={() => setPreviewTable(null)}
                dataSourceId={selectedConnection}
                objectName={previewTable || ''}
            />
        </aside>
    );
};
